/**
 * Effects System for Siege game
 *
 * Provides powerups (help the defender) and debuffs (hurt the defender)
 * that the frontend controller can apply during the game.
 */

/**
 * Effect type definitions
 */
export const EFFECT_TYPES = {
    // ==================== Powerups (Help Defender) ====================

    rapidFire: {
        id: 'rapidFire',
        name: 'Rapid Fire',
        type: 'powerup',
        icon: '\u26A1', // Lightning
        description: 'Increases click damage by 50% for 15 seconds',
        duration: 15000,
        apply: (engine) => {
            const originalDamage = engine.config.clickDamage;
            engine.config.clickDamage *= 1.5;
            return () => { engine.config.clickDamage = originalDamage; };
        }
    },

    wideBlast: {
        id: 'wideBlast',
        name: 'Wide Blast',
        type: 'powerup',
        icon: '\u{1F4A5}', // Explosion
        description: 'Doubles click AOE radius for 20 seconds',
        duration: 20000,
        apply: (engine) => {
            const originalRadius = engine.config.clickAoeRadius;
            engine.config.clickAoeRadius *= 2;
            return () => { engine.config.clickAoeRadius = originalRadius; };
        }
    },

    baseShield: {
        id: 'baseShield',
        name: 'Base Shield',
        type: 'powerup',
        icon: '\u{1F6E1}', // Shield
        description: 'Reduces damage to base by 75% for 30 seconds',
        duration: 30000,
        damageReduction: 0.75,
        apply: (engine) => {
            // Store the effect on engine for damage calculation
            engine._shieldActive = true;
            engine._shieldReduction = 0.75;
            return () => {
                engine._shieldActive = false;
                delete engine._shieldReduction;
            };
        }
    },

    slowField: {
        id: 'slowField',
        name: 'Slow Field',
        type: 'powerup',
        icon: '\u231B', // Hourglass
        description: 'Slows all enemies by 50% for 25 seconds',
        duration: 25000,
        apply: (engine) => {
            // Store original speeds
            const originalSpeeds = new Map();
            for (const unit of engine.units) {
                originalSpeeds.set(unit.id, unit.speed);
                unit.speed *= 0.5;
            }
            // Also slow newly spawned units
            engine._slowFieldActive = true;
            engine._slowFieldMultiplier = 0.5;

            return () => {
                // Restore speeds
                for (const unit of engine.units) {
                    const original = originalSpeeds.get(unit.id);
                    if (original) {
                        unit.speed = original;
                    } else if (engine._slowFieldMultiplier) {
                        unit.speed /= engine._slowFieldMultiplier;
                    }
                }
                engine._slowFieldActive = false;
                delete engine._slowFieldMultiplier;
            };
        }
    },

    healBase: {
        id: 'healBase',
        name: 'Repair Base',
        type: 'powerup',
        icon: '\u2764', // Heart
        description: 'Restores 25% of base health',
        duration: 0, // Instant
        apply: (engine) => {
            const healAmount = engine.maxBaseHealth * 0.25;
            engine.baseHealth = Math.min(engine.maxBaseHealth, engine.baseHealth + healAmount);
            return null; // No cleanup needed
        }
    },

    nuke: {
        id: 'nuke',
        name: 'Nuke',
        type: 'powerup',
        icon: '\u2622', // Radioactive
        description: 'Destroys all enemies on screen',
        duration: 0, // Instant
        apply: (engine) => {
            // Add nuke effect
            engine.effects.push({
                type: 'nuke',
                x: 0.5,
                y: 0.5,
                radius: 1,
                age: 0,
                duration: 1.5
            });

            // Destroy all units
            for (const unit of engine.units) {
                if (!unit.isDead) {
                    engine.destroyUnit(unit);
                }
            }
            return null;
        }
    },

    // ==================== Debuffs (Hurt Defender) ====================

    blindSpot: {
        id: 'blindSpot',
        name: 'Blind Spot',
        type: 'debuff',
        icon: '\u{1F441}', // Eye
        description: 'Cursor position is hidden for 10 seconds',
        duration: 10000,
        apply: (engine) => {
            engine._cursorHidden = true;
            return () => { engine._cursorHidden = false; };
        }
    },

    weakShots: {
        id: 'weakShots',
        name: 'Weak Shots',
        type: 'debuff',
        icon: '\u{1F4A8}', // Dash
        description: 'Reduces click damage by 50% for 20 seconds',
        duration: 20000,
        apply: (engine) => {
            const originalDamage = engine.config.clickDamage;
            engine.config.clickDamage *= 0.5;
            return () => { engine.config.clickDamage = originalDamage; };
        }
    },

    tinyRadius: {
        id: 'tinyRadius',
        name: 'Pinpoint',
        type: 'debuff',
        icon: '\u{1F52C}', // Microscope
        description: 'Halves click AOE radius for 20 seconds',
        duration: 20000,
        apply: (engine) => {
            const originalRadius = engine.config.clickAoeRadius;
            engine.config.clickAoeRadius *= 0.5;
            return () => { engine.config.clickAoeRadius = originalRadius; };
        }
    },

    enrage: {
        id: 'enrage',
        name: 'Enrage',
        type: 'debuff',
        icon: '\u{1F4A2}', // Anger
        description: 'All enemies move 50% faster for 15 seconds',
        duration: 15000,
        apply: (engine) => {
            // Speed up all units
            const originalSpeeds = new Map();
            for (const unit of engine.units) {
                originalSpeeds.set(unit.id, unit.speed);
                unit.speed *= 1.5;
            }
            engine._enrageActive = true;
            engine._enrageMultiplier = 1.5;

            return () => {
                for (const unit of engine.units) {
                    const original = originalSpeeds.get(unit.id);
                    if (original) {
                        unit.speed = original;
                    } else if (engine._enrageMultiplier) {
                        unit.speed /= engine._enrageMultiplier;
                    }
                }
                engine._enrageActive = false;
                delete engine._enrageMultiplier;
            };
        }
    },

    reinforcements: {
        id: 'reinforcements',
        name: 'Reinforcements',
        type: 'debuff',
        icon: '\u{1F47E}', // Alien
        description: 'Spawns 10 extra grunts immediately',
        duration: 0, // Instant
        apply: (engine) => {
            // Spawn grunts from random edges
            for (let i = 0; i < 10; i++) {
                setTimeout(() => {
                    if (engine.isRunning) {
                        engine.spawnUnitFromEdge('grunt');
                    }
                }, i * 100);
            }
            return null;
        }
    },

    armoredWave: {
        id: 'armoredWave',
        name: 'Armored Wave',
        type: 'debuff',
        icon: '\u{1F6E1}', // Shield
        description: 'Spawns 5 tanks immediately',
        duration: 0, // Instant
        apply: (engine) => {
            for (let i = 0; i < 5; i++) {
                setTimeout(() => {
                    if (engine.isRunning) {
                        engine.spawnUnitFromEdge('tank');
                    }
                }, i * 300);
            }
            return null;
        }
    }
};

/**
 * Get effect definition by ID
 * @param {string} effectId - Effect ID
 * @returns {Object|null}
 */
export function getEffect(effectId) {
    return EFFECT_TYPES[effectId] || null;
}

/**
 * Get all powerups
 * @returns {Object[]}
 */
export function getPowerups() {
    return Object.values(EFFECT_TYPES).filter(e => e.type === 'powerup');
}

/**
 * Get all debuffs
 * @returns {Object[]}
 */
export function getDebuffs() {
    return Object.values(EFFECT_TYPES).filter(e => e.type === 'debuff');
}

/**
 * EffectsManager - Manages active effects on the game
 */
export class EffectsManager {
    constructor(engine) {
        this.engine = engine;
        this.activeEffects = new Map(); // effectId -> { cleanup, expiresAt }
        this.effectHistory = [];
    }

    /**
     * Apply an effect to the game
     * @param {string} effectId - Effect to apply
     * @returns {boolean} Success
     */
    applyEffect(effectId) {
        const effect = getEffect(effectId);
        if (!effect) {
            console.warn(`[Effects] Unknown effect: ${effectId}`);
            return false;
        }

        // Remove existing effect of same type if active
        if (this.activeEffects.has(effectId)) {
            this.removeEffect(effectId);
        }

        console.log(`[Effects] Applying ${effect.type}: ${effect.name}`);

        // Apply effect
        const cleanup = effect.apply(this.engine);

        // Track effect
        const effectEntry = {
            effectId,
            effect,
            cleanup,
            appliedAt: Date.now(),
            expiresAt: effect.duration > 0 ? Date.now() + effect.duration : null
        };

        if (effect.duration > 0) {
            this.activeEffects.set(effectId, effectEntry);

            // Schedule removal
            setTimeout(() => {
                this.removeEffect(effectId);
            }, effect.duration);
        }

        // Track history
        this.effectHistory.push({
            effectId,
            type: effect.type,
            name: effect.name,
            appliedAt: Date.now()
        });

        // Notify engine of state change
        this.engine.notifyStateChange();

        return true;
    }

    /**
     * Remove an active effect
     * @param {string} effectId - Effect to remove
     */
    removeEffect(effectId) {
        const entry = this.activeEffects.get(effectId);
        if (!entry) return;

        console.log(`[Effects] Removing: ${entry.effect.name}`);

        // Call cleanup function
        if (entry.cleanup) {
            entry.cleanup();
        }

        this.activeEffects.delete(effectId);
        this.engine.notifyStateChange();
    }

    /**
     * Get all active effects
     * @returns {Object[]}
     */
    getActiveEffects() {
        const now = Date.now();
        return Array.from(this.activeEffects.entries()).map(([id, entry]) => ({
            effectId: id,
            name: entry.effect.name,
            type: entry.effect.type,
            icon: entry.effect.icon,
            remainingMs: entry.expiresAt ? Math.max(0, entry.expiresAt - now) : null,
            appliedAt: entry.appliedAt
        }));
    }

    /**
     * Check if an effect is active
     * @param {string} effectId - Effect ID
     * @returns {boolean}
     */
    isEffectActive(effectId) {
        return this.activeEffects.has(effectId);
    }

    /**
     * Update effects (call from game loop)
     */
    update() {
        // Check for expired effects (backup for setTimeout)
        const now = Date.now();
        for (const [effectId, entry] of this.activeEffects) {
            if (entry.expiresAt && now >= entry.expiresAt) {
                this.removeEffect(effectId);
            }
        }
    }

    /**
     * Clear all active effects
     */
    clearAll() {
        for (const effectId of this.activeEffects.keys()) {
            this.removeEffect(effectId);
        }
    }
}

export default EffectsManager;
